import {useState, useEffect} from 'react'
import {createPortal} from 'react-dom'
import * as Yup from 'yup'
import {defaultMemberEnrollment} from './InterfaceMemberModal'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import closeIcon from '../../../../_metronic/images/closeSymbol.svg'
import {useFormik} from 'formik'
import {CREATE_DO_NOT_ENROLL, GET_DO_NOT_ENROLL_BY_ID} from '../../../../utils/GraphQLAPI'
import {useLazyQuery, useMutation} from '@apollo/client'
import {Modal} from 'react-bootstrap'

const MySwal = withReactContent(Swal)

type Props = {
  show: boolean
  handleClose: () => void
  refetchList: () => void
  method: string
  editId: any
  isSolarProduct: any
}

const modalsRoot = document.getElementById('root-modals') || document.body

const enrollSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .matches(
      /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/,
      'Phone Number should contain only digits'
    )
    .matches(
      /^(\+|1\s)?(?:(?:\(?(?:00|\+)([1-4]\d\d|[1-9]\d+)\)?)[\-\.\ \\\/]?)?((?:\(?\d{1,}\)?[\-\.\ \\\/]?)+)(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i,
      'Special characters are not allowed'
    )
    .min(10, 'Phone Number should be exactly 10 digits')
    .max(10, 'Phone Number should be exactly 10 digits'),
  utilityAccountNumber: Yup.string()
    .matches(/^\S*$/, 'Spaces not allowed')
    .min(3, "UAN  shouldn't be less than 3 characters")
    .max(30, "UAN shouldn't exceed 30 characters"),
  zipCode: Yup.string().matches(
    /^(?!0{5})([0-9]{5}(?:-[0-9]{4})?|[A-Z][0-9][A-Z])$/,
    'Enter a valid zip code'
  ),
  emailId: Yup.string()
    .email('Email ID is not Valid')
    .matches(/^(?!.*@[^,]*,)/, 'Email ID is not Valid')
    .min(10, "Email ID shouldn't less than 10 characters")
    .max(40, "Email ID shouldn't exceed 40 characters")
    .nullable(),
})

const CreateMemberModal = ({
  show,
  handleClose,
  refetchList,
  method,
  editId,
  isSolarProduct,
}: Props) => {
  const [btnHide, setBtnHide] = useState<boolean>(false)
  const [fieldDisabled, setFieldDisabled] = useState<boolean>(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (method === 'create') {
      formik.setValues({...defaultMemberEnrollment})
    }
    // reseting the form when closed
    formik.resetForm()
    formik.setValues({...defaultMemberEnrollment})
  }, [show])

  const formik = useFormik({
    initialValues: defaultMemberEnrollment,
    validationSchema: enrollSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (method === 'create') {
        if (
          values.phoneNumber === '' &&
          values.utilityAccountNumber === '' &&
          values.zipCode === '' &&
          values.emailId === ''
        ) {
          setError('Atleast one field should be filled out')
        } else {
          createDoNotEnrollment({
            variables: {
              createDneInput: {
                phoneNumber: values.phoneNumber,
                utilityAccountNumber: values.utilityAccountNumber,
                zipCode: values.zipCode,
                reason: values.reason,
                ...(values.emailId && {emailId: values.emailId}),
              },
            },
          })
        }
      }
    },
  })

  // CREATE
  const [createDoNotEnrollment] = useMutation(CREATE_DO_NOT_ENROLL, {
    onCompleted: (data) => {
      if (data.createDne.id) {
        const options: any = {
          customClass: {
            popup: 'swal2-border-radius',
          },
          title: 'Customer created successfully',
          icon: 'success',
          confirmButtonColor: '#3E97FF',
        }
        MySwal.fire(options)
        formik.setValues({...defaultMemberEnrollment})
        setError('')
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'Error in creating Customer',
        })
      }
      refetchList()
      handleClose()
      setError('')
    },
    onError: (e) => {
      MySwal.fire({
        icon: 'error',
        title: e.message,
      })
    },
  })

  // GET_BY_ID_EDIT
  const [getDneById] = useLazyQuery(GET_DO_NOT_ENROLL_BY_ID, {
    fetchPolicy: 'network-only',
    variables: {
      id: editId,
    },
    onCompleted: (memberData) => {
      const editMemberEnrollment = {
        id: memberData.getDnebyId.id,
        phoneNumber: memberData.getDnebyId.phoneNumber,
        utilityAccountNumber: memberData.getDnebyId.utilityAccountNumber,
        zipCode: memberData.getDnebyId.zipCode,
        reason: memberData.getDnebyId.reason,
        emailId: memberData.getDnebyId.emailId,
      }
      formik.setValues({...editMemberEnrollment})
    },
  })

  useEffect(() => {
    if (editId) {
      getDneById()
      setFieldDisabled(true)
      setBtnHide(true)
    } else {
      setFieldDisabled(false)
      setBtnHide(false)
    }
  }, [editId])

  return createPortal(
    <Modal
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-650px'
      show={show}
      onHide={() => {
        handleClose()
        setError('')
      }}
      backdrop={true}
    >
      <div className='modal-header'>
        <h4 className='modal-title '>Do Not Enroll List</h4>
        <div
          className='btn btn-sm btn-icon btn-active-color-primary'
          onClick={() => {
            handleClose()
            setError('')
          }}
        >
          <img className='svg-icon-1' src={closeIcon} alt='Close' />
        </div>
      </div>

      <div className='modal-body'>
        <div className='modal-md'>
          <form noValidate id='kt_modal_create_app_form' onSubmit={formik.handleSubmit}>
            <div className='modal-content'>
              <div className='modal-body pt-0'>
                <div className='card-toolbar flex-row-fluid justify-content-end gap-5'>
                  <div className='row mb-4'>
                    <div className='col-6'>
                      <h3 className='mt-2'>Customer Details</h3>
                    </div>
                  </div>
                </div>
                <div className='row'>
                  <div className='h5 text-danger text-center'>{error}</div>
                </div>
                <div className='mb-9'>
                  <label className='form-label'>Phone Number</label>
                  <div className='d-flex'>
                    <input
                      className='form-control-solid form-control w-50px'
                      value='+1'
                      readOnly
                      style={{borderTopRightRadius: '0', borderBottomRightRadius: '0'}}
                    />
                    <input
                      type='text'
                      maxLength={10}
                      className='form-control form-control-solid'
                      placeholder='Enter phone number'
                      disabled={fieldDisabled}
                      {...formik.getFieldProps('phoneNumber')}
                      style={{borderTopLeftRadius: '0', borderBottomLeftRadius: '0'}}
                    />
                  </div>
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <div className='fv-plugins-message-container'>
                      <div data-field='appname' data-validator='notEmpty' className='fv-help-block'>
                        {formik.errors.phoneNumber}
                      </div>
                    </div>
                  )}
                </div>
                <div className='mb-9'>
                  <label className='form-label'>UAN (Utility Account Number)</label>
                  <input
                    type='text'
                    className='form-control form-control-solid'
                    placeholder='Enter UA Number'
                    disabled={fieldDisabled}
                    {...formik.getFieldProps('utilityAccountNumber')}
                  />
                  {formik.touched.utilityAccountNumber && formik.errors.utilityAccountNumber && (
                    <div className='fv-plugins-message-container'>
                      <div data-field='appname' data-validator='notEmpty' className='fv-help-block'>
                        {formik.errors.utilityAccountNumber}
                      </div>
                    </div>
                  )}
                </div>
                {isSolarProduct && (
                  <div className='mb-9'>
                    <label className='form-label'>Email</label>
                    <input
                      type='text'
                      className='form-control form-control-solid'
                      placeholder='Enter Email Address'
                      disabled={fieldDisabled}
                      {...formik.getFieldProps('emailId')}
                    />
                    {formik.touched.emailId && formik.errors.emailId && (
                      <div className='fv-plugins-message-container'>
                        <div
                          data-field='appname'
                          data-validator='notEmpty'
                          className='fv-help-block'
                        >
                          {formik.errors.emailId}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className='mb-9'>
                  <label className='form-label'>Zip Code</label>
                  <input
                    type='text'
                    className='form-control form-control-solid'
                    placeholder='Enter zip code'
                    disabled={fieldDisabled}
                    {...formik.getFieldProps('zipCode')}
                  />
                  {formik.touched.zipCode && formik.errors.zipCode && (
                    <div className='fv-plugins-message-container'>
                      <div data-field='appname' data-validator='notEmpty' className='fv-help-block'>
                        {formik.errors.zipCode}
                      </div>
                    </div>
                  )}
                </div>
                <div className='mb-9'>
                  <label className='form-label'>Reason</label>
                  <input
                    type='text'
                    className='form-control form-control-solid'
                    placeholder='Enter reason (optional)'
                    disabled={fieldDisabled}
                    {...formik.getFieldProps('reason')}
                  />
                </div>
              </div>

              {btnHide ? (
                ''
              ) : (
                <div className='modal-footer'>
                  <button
                    type='button'
                    className='btn btn-light'
                    data-bs-dismiss='modal'
                    onClick={() => {
                      handleClose()
                      setError('')
                    }}
                  >
                    Discard
                  </button>
                  <button type='submit' className='btn btn-primary'>
                    Submit
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

export {CreateMemberModal}
